import { useState, useEffect } from "react";
import {
  Shield, Clock, User, FileText, Package, DollarSign,
  Loader2, Search, Filter, RefreshCw, ChevronLeft, ChevronRight,
  AlertCircle, CheckCircle, Edit, Trash2, Plus, Eye, Send
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const actionIcons = {
  create: Plus,
  update: Edit,
  delete: Trash2,
  view: Eye,
  send: Send,
  login: User,
  logout: User,
  mark_paid: CheckCircle,
  quick_cash_sale: DollarSign,
  invite_team_member: User,
  stock_adjustment: Package,
  default: FileText
};

const actionColors = {
  create: "text-emerald-600 bg-emerald-50",
  update: "text-blue-600 bg-blue-50",
  delete: "text-red-600 bg-red-50",
  view: "text-gray-600 bg-gray-50",
  send: "text-purple-600 bg-purple-50",
  login: "text-emerald-600 bg-emerald-50",
  logout: "text-amber-600 bg-amber-50",
  mark_paid: "text-emerald-600 bg-emerald-50",
  quick_cash_sale: "text-emerald-600 bg-emerald-50",
  invite_team_member: "text-blue-600 bg-blue-50",
  stock_adjustment: "text-amber-600 bg-amber-50",
  default: "text-gray-600 bg-gray-50"
};

const resourceLabels = {
  document: "Document",
  client: "Client",
  invoice: "Invoice",
  sale: "Sale",
  inventory: "Inventory",
  user: "User",
  team: "Team",
  cash_transaction: "Transaction",
  subscription: "Subscription"
};

export default function AuditTrail() {
  const { t } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterAction, setFilterAction] = useState("all");
  const [filterResource, setFilterResource] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 50;

  useEffect(() => {
    loadAuditLogs();
  }, [page, filterAction, filterResource]);

  const loadAuditLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: pageSize.toString() });
      if (filterAction !== "all") params.append("action", filterAction);
      if (filterResource !== "all") params.append("resource_type", filterResource);
      
      const response = await axios.get(`${API}/audit-log?${params}`);
      setLogs(response.data.logs || response.data || []);
      setHasMore((response.data.logs || response.data || []).length === pageSize);
    } catch (error) {
      console.error("Failed to load audit logs:", error);
      toast.error("Failed to load audit trail");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatAction = (action) => {
    return action
      .replace(/_/g, " ")
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const getActionIcon = (action) => {
    const baseAction = action.split("_")[0];
    return actionIcons[action] || actionIcons[baseAction] || actionIcons.default;
  };

  const getActionColor = (action) => {
    const baseAction = action.split("_")[0];
    return actionColors[action] || actionColors[baseAction] || actionColors.default;
  };

  // Filter logs by search
  const filteredLogs = logs.filter(log => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      log.action?.toLowerCase().includes(searchLower) ||
      log.resource_type?.toLowerCase().includes(searchLower) ||
      log.resource_id?.toLowerCase().includes(searchLower) ||
      log.user_email?.toLowerCase().includes(searchLower) ||
      JSON.stringify(log.details || {}).toLowerCase().includes(searchLower)
    );
  });

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="audit-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-trail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            Audit Trail
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track all actions and changes in your account
          </p>
        </div>
        <Button variant="outline" onClick={loadAuditLogs} className="gap-2" data-testid="refresh-audit-btn">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="audit-info-banner">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-800">What is the Audit Trail?</h3>
            <p className="text-sm text-blue-700 mt-1">
              The audit trail automatically logs all important actions in your account, including document creation, 
              payments, team member changes, and more. This helps you maintain compliance and track activity.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search logs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="audit-search-input"
          />
        </div>
        <Select value={filterAction} onValueChange={setFilterAction}>
          <SelectTrigger className="w-full sm:w-40" data-testid="audit-action-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="create">Create</SelectItem>
            <SelectItem value="update">Update</SelectItem>
            <SelectItem value="delete">Delete</SelectItem>
            <SelectItem value="mark_paid">Mark Paid</SelectItem>
            <SelectItem value="quick_cash_sale">Quick Sale</SelectItem>
            <SelectItem value="send">Send</SelectItem>
            <SelectItem value="login">Login</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterResource} onValueChange={setFilterResource}>
          <SelectTrigger className="w-full sm:w-40" data-testid="audit-resource-filter">
            <FileText className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Resource" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Resources</SelectItem>
            <SelectItem value="document">Documents</SelectItem>
            <SelectItem value="invoice">Invoices</SelectItem>
            <SelectItem value="client">Clients</SelectItem>
            <SelectItem value="sale">Sales</SelectItem>
            <SelectItem value="inventory">Inventory</SelectItem>
            <SelectItem value="user">Users</SelectItem>
            <SelectItem value="team">Team</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-card border rounded-lg p-4" data-testid="stat-total-logs">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Logs</p>
          <p className="text-2xl font-bold mt-1">{filteredLogs.length}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="stat-creates">
          <p className="text-xs text-emerald-600 uppercase tracking-wide">Creates</p>
          <p className="text-2xl font-bold mt-1 text-emerald-700">
            {filteredLogs.filter(l => l.action?.startsWith("create")).length}
          </p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="stat-updates">
          <p className="text-xs text-blue-600 uppercase tracking-wide">Updates</p>
          <p className="text-2xl font-bold mt-1 text-blue-700">
            {filteredLogs.filter(l => l.action?.startsWith("update")).length}
          </p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="stat-sales">
          <p className="text-xs text-amber-600 uppercase tracking-wide">Sales</p>
          <p className="text-2xl font-bold mt-1 text-amber-700">
            {filteredLogs.filter(l => l.action?.includes("sale") || l.action?.includes("paid")).length}
          </p>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-card border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="audit-table">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-left p-3 text-xs font-medium text-muted-foreground">Timestamp</th>
                <th className="text-left p-3 text-xs font-medium text-muted-foreground">Action</th>
                <th className="text-left p-3 text-xs font-medium text-muted-foreground">Resource</th>
                <th className="text-left p-3 text-xs font-medium text-muted-foreground">User</th>
                <th className="text-left p-3 text-xs font-medium text-muted-foreground">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-muted-foreground">
                    {searchQuery || filterAction !== "all" || filterResource !== "all"
                      ? "No matching logs found"
                      : "No audit logs yet. Actions will be recorded here automatically."}
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log, index) => {
                  const ActionIcon = getActionIcon(log.action);
                  const colorClass = getActionColor(log.action);
                  
                  return (
                    <tr key={log.id || index} className="hover:bg-muted/30" data-testid={`audit-row-${index}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">
                            {formatDate(log.timestamp || log.created_at)}
                          </span>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded ${colorClass}`}>
                            <ActionIcon className="w-3.5 h-3.5" />
                          </div>
                          <span className="text-sm font-medium">
                            {formatAction(log.action)}
                          </span>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="text-sm">
                          <span className="font-medium">
                            {resourceLabels[log.resource_type] || log.resource_type}
                          </span>
                          {log.resource_id && (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-32">
                              {log.resource_id.substring(0, 8)}...
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            {log.user_email || log.user_id?.substring(0, 8) || "System"}
                          </span>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="text-xs text-muted-foreground max-w-48 truncate">
                          {log.details ? (
                            Object.entries(log.details).map(([key, value]) => (
                              <span key={key} className="mr-2">
                                {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </span>
                            ))
                          ) : (
                            "—"
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredLogs.length > 0 && (
          <div className="border-t p-3 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {filteredLogs.length} logs
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={!hasMore}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
